import { useAuth } from "@/lib/auth";

/**
 * Roles (2026-09-28, Brett): viewer < user < pmo < admin. The backend is
 * the authority (app/auth/dependencies.py); these helpers only decide
 * what to SHOW. Keep ACCESS_MATRIX in step with the server gates — it is
 * rendered on /admin so the PMO can see what each level unlocks.
 */
export const ROLES = ["viewer", "user", "pmo", "admin"] as const;
export type RoleName = (typeof ROLES)[number];

const RANK: Record<RoleName, number> = { viewer: 1, user: 2, pmo: 3, admin: 4 };

export const ROLE_LABELS: Record<RoleName, string> = {
  viewer: "Viewer",
  user: "User",
  pmo: "PMO",
  admin: "Admin",
};

export function rankOf(role: string | null | undefined): number {
  return RANK[(role ?? "viewer") as RoleName] ?? 1;
}

export function atLeast(role: string | null | undefined, min: RoleName): boolean {
  return rankOf(role) >= RANK[min];
}

/** What each level can do — one row per capability, `min` = lowest role that has it. */
export const ACCESS_MATRIX: Array<{ capability: string; min: RoleName }> = [
  { capability: "Read every page: Executive, Top Projects, Portfolio, project pages, Capacity, Roster, Help", min: "viewer" },
  { capability: "Request a Project", min: "viewer" },
  { capability: "Edit projects: details, status pulse, milestones, RAID, dates, benefit", min: "user" },
  { capability: "Assign people and hours to a project; re-rank the board", min: "user" },
  { capability: "Weekly Update: read the draft, copy it, email it to yourself", min: "user" },
  { capability: "Service Desk and Application Support dashboards", min: "user" },
  { capability: "Approve or decline a request; place a Requested project on the board", min: "pmo" },
  { capability: "Roster: add people, set weekly hours, support %, include in capacity, PTO", min: "pmo" },
  { capability: "Enhancements: route to a department, record approval, mark done", min: "pmo" },
  { capability: "Weekly Update: Send to… a list", min: "pmo" },
  { capability: "Settings and System: role defaults, cost rate, integrations, imports", min: "admin" },
  { capability: "User accounts and access requests", min: "admin" },
];

export function useCan() {
  const { user } = useAuth();
  const role = user?.role;
  return {
    role,
    canEdit: atLeast(role, "user"),
    isPmo: atLeast(role, "pmo"),
    isAdmin: atLeast(role, "admin"),
  };
}
